import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BulletList from './BulletList';
import RecentContextPanel from './RecentContextPanel';

const StatRow = ({ label, planned, executed, delta }) => {
  const fmt = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);
  return (
    <div className="grid grid-cols-12 gap-2 py-2 border-b border-gray-100">
      <div className="col-span-5 text-sm font-bold text-gray-800">{label}</div>
      <div className="col-span-2 text-sm text-gray-700 tabular-nums">{fmt(planned)}</div>
      <div className="col-span-2 text-sm text-gray-700 tabular-nums">{fmt(executed)}</div>
      <div className="col-span-3 text-sm text-gray-700 tabular-nums">{fmt(delta, typeof delta === 'number' ? '%' : '')}</div>
    </div>
  );
};

export default function CoachDeepDive({ coachAnalysis, recentContext = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const analysis = coachAnalysis?.analysis;
  const pve = analysis?.plan_vs_execution;
  const compliance = pve?.compliance;
  const intensity = pve?.intensity;
  const theory = Array.isArray(analysis?.training_theory) ? analysis.training_theory : [];
  const context = analysis?.context;

  const zones = useMemo(() => {
    const z = intensity?.hr_time_in_zones;
    const safe = z && typeof z === 'object' ? z : {};
    return ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'].map((k) => ({ k, v: Number(safe[k]) || 0 }));
  }, [intensity?.hr_time_in_zones]);

  if (!analysis) return null;

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-50"
      >
        <div className="text-sm font-extrabold text-gray-900">{t('workout.deep_dive')}</div>
        <div className="text-gray-500">{open ? '▾' : '▸'}</div>
      </button>

      {open ? (
        <div className="mt-3 bg-white border border-gray-200 rounded-xl p-5 space-y-6">
          {recentContext ? <RecentContextPanel recentContext={recentContext} /> : null}

          <div>
            <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
              {t('workout.plan_vs_execution')}
            </div>
            <div className="mt-3 bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div className="grid grid-cols-12 gap-2 text-[11px] font-bold text-gray-500 uppercase tracking-wide pb-2">
                <div className="col-span-5">{t('workout.metric')}</div>
                <div className="col-span-2">{t('workout.planned')}</div>
                <div className="col-span-2">{t('workout.executed')}</div>
                <div className="col-span-3">{t('workout.delta')}</div>
              </div>

              <StatRow
                label={t('workout.distance')}
                planned={compliance?.distance?.planned_km ?? null}
                executed={compliance?.distance?.executed_km ?? null}
                delta={compliance?.distance?.delta_pct ?? null}
              />
              <StatRow
                label={t('workout.duration')}
                planned={compliance?.duration?.planned_min ?? null}
                executed={compliance?.duration?.executed_min ?? null}
                delta={compliance?.duration?.delta_pct ?? null}
              />
              <div className="grid grid-cols-12 gap-2 py-2">
                <div className="col-span-5 text-sm font-bold text-gray-800">{t('workout.elevation')}</div>
                <div className="col-span-2 text-sm text-gray-700 tabular-nums">{compliance?.elevation?.planned_m ?? '—'}</div>
                <div className="col-span-2 text-sm text-gray-700 tabular-nums">{compliance?.elevation?.executed_m ?? '—'}</div>
                <div className="col-span-3 text-sm text-gray-700 tabular-nums">
                  {typeof compliance?.elevation?.delta_ratio === 'number'
                    ? `${compliance.elevation.delta_ratio.toFixed(2)}×`
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
              {t('workout.intensity')}
            </div>
            {intensity?.planned_target ? (
              <div className="text-sm text-gray-800 mt-2">
                <span className="font-bold">{t('workout.planned_target')}:</span> {intensity.planned_target}
              </div>
            ) : null}

            <div className="mt-3 grid grid-cols-5 gap-2">
              {zones.map(({ k, v }) => (
                <div key={k} className="border border-gray-200 rounded-lg p-2 text-center">
                  <div className="text-[10px] font-extrabold text-gray-500">{k}</div>
                  <div className="text-sm font-bold tabular-nums">{v}%</div>
                </div>
              ))}
            </div>

            <BulletList items={intensity?.notes} className="mt-3" />
          </div>

          {theory.length > 0 ? (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
                {t('workout.training_theory')}
              </div>
              <div className="mt-3 space-y-3">
                {theory.slice(0, 3).map((p, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="font-extrabold text-gray-900">{p.principle}</div>
                    <div className="text-sm text-gray-700 mt-1">
                      <span className="font-bold">{t('workout.why_it_matters')}:</span> {p.why_it_matters}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      <span className="font-bold">{t('workout.how_it_applies_today')}:</span> {p.how_it_applies_today}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {context ? (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
                {t('workout.week_context')}
              </div>
              {context.last_7_days_load_comment ? (
                <div className="text-sm text-gray-800 mt-2">{context.last_7_days_load_comment}</div>
              ) : null}
              {context.fatigue_risk ? (
                <div className="text-sm text-gray-700 mt-2">
                  <span className="font-bold">{t('workout.fatigue_risk')}:</span> {context.fatigue_risk}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


